import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, HardHat, PencilSimple, Trash, CheckCircle, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, hasCap } from '@/auth/AuthContext';
import { mfgApi, PPE_STATUS_BADGE } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import { CategoryPicker, EmployeePicker, LocationPicker, registrationApi } from '@/components/registration';
import useDirectory from '@/hooks/useDirectory';
import { todayJst } from '@/utils/jstDate';

const today = () => todayJst();
const EMPTY = {
  name: '', category: '', assigned_to: '', quantity: 1, inspection_interval_days: 90,
  last_inspected_on: '', status: 'ok', notes: '',
  // 標準ルール §7 · 共通部品で登録する（品目は必ずマスターから選ぶ）
  item_id: '', item_source: '', maker: '', model_no: '', uom: '個',
  location_id: '', location: '', assigned_to_employee_id: '',
};

function PpeFormDialog({ open, ppe, onClose, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const { employees } = useDirectory();
  useEffect(() => { if (open) setForm(ppe ? { ...EMPTY, ...ppe } : EMPTY); }, [open, ppe]);
  useEffect(() => {
    if (!open) return;
    mfgApi.categories().then((rows) => setCategories(
      (rows || []).map((c) => (typeof c === 'string' ? { id: c, name: c } : c)),
    )).catch(() => setCategories([]));
  }, [open]);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const save = async () => {
    if (!form.name.trim()) { toast.error(t('mfg.ppe.name')); return; }
    setBusy(true);
    try {
      const emp = employees.find((e) => e.name === form.assigned_to);
      const body = {
        ...form, name: form.name.trim(),
        quantity: Number(form.quantity) || 1,
        inspection_interval_days: Number(form.inspection_interval_days) || 0,
        last_inspected_on: form.last_inspected_on || null,
        assigned_to: form.assigned_to || null,
        assigned_to_employee_id: emp?.id || null,
        item_id: form.item_id || null, item_source: form.item_source || null,
        location_id: form.location_id || null, location: form.location || null,
        maker: form.maker || null, model_no: form.model_no || null, uom: form.uom || '個',
      };
      const res = ppe ? await mfgApi.updatePpe(ppe.id, body) : await mfgApi.createPpe(body);
      toast.success(t('mfg.common.saved')); onSaved?.(res);
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="ppe-form-dialog">
        <DialogHeader><DialogTitle>{ppe ? t('mfg.ppe.edit') : t('mfg.ppe.new')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {/* 1 · 品目はマスターから選ぶ（品名の自由入力はしない） */}
          {!ppe && (
            <MasterItemPicker
              testIdPrefix="ppe-master"
              label="品目（マスターから検索 または 新規入力）"
              search={registrationApi.masterSearch}
              sources={['mfg', 'eng', 'it', 'office', 'equip']}
              minChars={2}
              onPick={(r) => patch({
                item_id: r.id, item_source: r.source, name: r.name || '',
                maker: r.maker || '', model_no: r.model_no || '', uom: r.uom || '個',
              })}
              linked={form.item_id ? { label: form.name, sku: form.model_no } : null}
              onUnlink={() => patch({ item_id: '', item_source: '' })}
              helper="保護具も品目マスターから選びます（品名の手入力はしません）。未登録なら 在庫品目 または 技術資産 で登録してから選んでください。"
            />
          )}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{t('mfg.ppe.name')}</Label>
            <Input data-testid="ppe-name-input" value={form.name} onChange={set('name')}
              readOnly={!!form.item_id} className={`h-10 mt-1 ${form.item_id ? 'bg-zinc-50' : ''}`} />
            {form.item_id && (
              <p className="mt-1 text-[11px] text-zinc-500" data-testid="ppe-name-from-master">
                品名はマスターの値です（直接は変えません）。
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('mfg.ppe.category')}</Label>
              <CategoryPicker value={form.category} options={categories}
                onChange={(v) => patch({ category: v })} testId="ppe-category" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('mfg.ppe.assignedTo')}</Label>
              <EmployeePicker value={form.assigned_to} employees={employees}
                onChange={(v) => patch({ assigned_to: v })} testId="ppe-assigned-to" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">保管場所</Label>
            <LocationPicker value={form.location_id} legacyText={form.location_id ? '' : (form.location || '')}
              onChange={(id, row) => patch({ location_id: id, location: (row && (row.path || row.name)) || '' })}
              testId="ppe-location" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.ppe.quantity')}</Label><Input type="number" value={form.quantity} onChange={set('quantity')} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.ppe.interval')}</Label><Input type="number" data-testid="ppe-interval-input" value={form.inspection_interval_days} onChange={set('inspection_interval_days')} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.ppe.lastInspected')}</Label><DateField value={form.last_inspected_on || ''} onChange={set('last_inspected_on')} className="mt-1" inputClassName="h-10" /></div>
          </div>
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.common.notes')}</Label><Input value={form.notes} onChange={set('notes')} className="h-10 mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('mfg.common.cancel')}</Button>
          <Button data-testid="ppe-save-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? t('mfg.common.saving') : t('mfg.common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InspectDialog({ open, ppe, onClose, onDone }) {
  const { t } = useT();
  const [on, setOn] = useState(today());
  const [result, setResult] = useState('ok');
  const [inspector, setInspector] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setOn(today()); setResult('ok'); setInspector(''); setNotes(''); } }, [open]);
  const save = async () => {
    setBusy(true);
    try { await mfgApi.inspectPpe(ppe.id, { inspected_on: on, result, inspector: inspector || null, notes: notes || null }); toast.success(t('mfg.common.saved')); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="ppe-inspect-dialog">
        <DialogHeader><DialogTitle>{t('mfg.ppe.inspectTitle')}{ppe ? ` — ${ppe.name}` : ''}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.performedOn')}</Label><DateField value={on || ''} onChange={(v) => setOn(v)} className="mt-1" inputClassName="h-10" /></div>
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.ppe.result')}</Label>
            <Select value={result} onValueChange={setResult}><SelectTrigger data-testid="ppe-inspect-result" className="h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ok">{t('mfg.ppe.result.ok')}</SelectItem><SelectItem value="ng">{t('mfg.ppe.result.ng')}</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.ppe.inspector')}</Label><Input value={inspector} onChange={(e) => setInspector(e.target.value)} className="h-10 mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('mfg.common.cancel')}</Button>
          <Button data-testid="ppe-inspect-confirm" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{t('mfg.ppe.inspect')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Ppe() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editPpe, setEditPpe] = useState(null);
  const [inspectPpe, setInspectPpe] = useState(null);

  const load = useCallback(() => {
    mfgApi.ppe().then(setList).catch(() => {});
    mfgApi.ppeStats().then(setStats).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    if (!window.confirm(`${p.ppe_no} を削除しますか？`)) return;
    try { await mfgApi.deletePpe(p.id); toast.success(t('mfg.common.deleted')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="mfg-ppe">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.ppe.title')}</h1>
        {canWrite && (
          <Button data-testid="ppe-new-btn" onClick={() => { setEditPpe(null); setFormOpen(true); }} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={18} className="mr-1.5" weight="bold" /> {t('mfg.ppe.new')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4" data-testid="ppe-stat-total"><div className="text-2xl font-bold text-zinc-900 tabular-nums">{stats?.total ?? 0}</div><div className="text-xs text-zinc-500 mt-0.5">{t('mfg.ppe.stat.total')}</div></div>
        <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4" data-testid="ppe-stat-overdue"><div className="text-2xl font-bold text-rose-700 tabular-nums">{stats?.overdue ?? 0}</div><div className="text-xs text-rose-600 mt-0.5 flex items-center gap-1"><Warning size={13} /> {t('mfg.ppe.stat.overdue')}</div></div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="ppe-stat-duesoon"><div className="text-2xl font-bold text-amber-700 tabular-nums">{stats?.due_soon ?? 0}</div><div className="text-xs text-amber-600 mt-0.5">{t('mfg.ppe.stat.dueSoon')}</div></div>
      </div>

      <DataTable
        dataTestId="ppe-table"
        rows={list}
        rowKey={(p) => p.id}
        rowTestId={(p) => `ppe-row-${p.id}`}
        empty={{ icon: <HardHat size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.ppe.empty'), testid: 'ppe-empty' }}
        columns={[
          { key: 'ppe_no', header: t('mfg.ppe.no'), cell: (p) => <span className="font-mono text-xs text-zinc-600">{p.ppe_no}</span> },
          { key: 'name', primary: true, header: t('mfg.ppe.name'), cell: (p) => <span className="font-medium text-zinc-800">{p.name}</span> },
          { key: 'category', header: t('mfg.ppe.category'), cell: (p) => <span className="text-zinc-600">{p.category || '—'}</span> },
          { key: 'assigned_to', header: t('mfg.ppe.assignedTo'), cell: (p) => <span className="text-zinc-600">{p.assigned_to || '—'}</span> },
          { key: 'last_inspected', header: t('mfg.ppe.lastInspected'), cell: (p) => <span className="text-zinc-600">{p.last_inspected_on || '—'}</span> },
          { key: 'next_inspection', header: t('mfg.ppe.nextInspection'), cell: (p) => <span className={p.overdue ? 'text-rose-600 font-semibold' : (p.due_soon ? 'text-amber-600 font-semibold' : 'text-zinc-600')}>{p.next_inspection_on || '—'}</span> },
          { key: 'status', header: '', cell: (p) => <Badge variant="outline" className={PPE_STATUS_BADGE[p.status]}>{p.status_label}</Badge> },
          { key: 'actions', actions: true, header: '', cell: (p) => (<>
            {canWrite && <Button size="sm" variant="outline" data-testid={`ppe-inspect-${p.id}`} onClick={() => setInspectPpe(p)} className="h-9 text-xs"><CheckCircle size={14} className="mr-1" /> {t('mfg.ppe.inspect')}</Button>}
            {canWrite && <Button size="sm" variant="ghost" data-testid={`ppe-edit-${p.id}`} onClick={() => { setEditPpe(p); setFormOpen(true); }} className="h-9 px-2 text-zinc-500"><PencilSimple size={15} /></Button>}
            {canDeleteMfg && <Button size="sm" variant="ghost" data-testid={`ppe-delete-${p.id}`} onClick={() => del(p)} className="h-9 px-2 text-rose-600"><Trash size={15} /></Button>}
          </>) },
        ]}
      />

      <PpeFormDialog open={formOpen} ppe={editPpe} onClose={() => { setFormOpen(false); setEditPpe(null); }} onSaved={() => { setFormOpen(false); setEditPpe(null); load(); }} />
      <InspectDialog open={!!inspectPpe} ppe={inspectPpe} onClose={() => setInspectPpe(null)} onDone={() => { setInspectPpe(null); load(); }} />
    </div>
  );
}
